// StatusPages admin list — T091
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStatusPages, deleteStatusPage, type StatusPage } from '../services/api';

export default function StatusPages() {
  const [pages, setPages] = useState<StatusPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getStatusPages()
      .then((res) => setPages(res.data || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this status page?')) return;
    try {
      await deleteStatusPage(id);
      setPages((prev) => prev.filter((p) => p.id !== id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div className="animate-pulse bg-surface rounded-lg h-32" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-white text-h1">Status Pages</h1>
        <Link
          to="/status-pages/new"
          className="bg-brand text-white rounded px-4 py-2 text-sm min-h-[44px] inline-flex items-center hover:bg-brand-hover transition-colors"
        >
          + Create
        </Link>
      </div>

      {error && <div className="bg-status-down/20 text-status-down rounded px-4 py-3 text-sm">{error}</div>}

      {pages.length === 0 ? (
        <div className="bg-surface rounded-lg p-6 border border-border text-center text-text-muted">
          No status pages yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map((page) => (
            <div key={page.id} className="bg-surface rounded-lg p-4 border border-border flex items-center justify-between gap-4">
              <div className="min-w-0">
                <Link to={`/status-pages/${page.id}`} className="text-text-primary font-semibold hover:text-brand truncate block">
                  {page.title}
                </Link>
                <div className="flex items-center gap-2 mt-1">
                  <Link
                    to={`/status/${page.slug}`}
                    target="_blank"
                    className="text-text-muted text-xs hover:text-brand transition-colors"
                  >
                    /{page.slug}
                  </Link>
                  <span className={`text-xs px-2 py-0.5 rounded ${page.isPublished ? 'bg-status-up/20 text-status-up' : 'bg-surface text-text-muted border border-border'}`}>
                    {page.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {page.isPublished && (
                  <Link
                    to={`/status/${page.slug}`}
                    target="_blank"
                    className="text-text-secondary hover:text-text-primary text-sm min-h-[44px] px-2 inline-flex items-center"
                  >
                    View
                  </Link>
                )}
                <Link
                  to={`/status-pages/${page.id}`}
                  className="text-text-secondary hover:text-text-primary text-sm min-h-[44px] px-2 inline-flex items-center"
                >
                  Edit
                </Link>
                <button
                  onClick={() => handleDelete(page.id)}
                  className="text-status-down hover:text-status-down/80 text-sm min-h-[44px] px-2"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
